import * as Redux from 'redux';
import {
  GET_MEMBER,
  GET_MEMBERS,
  GET_ABSENT_MEMBERS,
  BULK_CANCEL,
  REGISTER_MEMBER,
  UPDATE_MEMBER,
  BULK_ADD_DAYS_TO_END_DATES,
  BULK_SEND_EMAILS,
  BULK_SEND_SMS,
  CREATE_PAYMENT,
  CANCEL_PAYMENT,
  UPDATE_SCHEDULED_PAYMENT,
  UPDATE_PAYMENT,
  EXPORT_CSV
} from './members.constants';
import {
  MembersActionTypes,
  MembersResponseData,
  MemberType
} from './members.types';
import { ReduxPromiseAction, SimpleResponse } from '../app/app.types';
import { callApi } from '../util/ApiCaller';
import { pickAuthOrImpersonationToken } from '../util/token.util';
import { Currency } from 'src/billing/billing.types';

export const updateScheduledPayment: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (id: number, chargeDate: number, amount: number): ReduxPromiseAction<
  MembersActionTypes, SimpleResponse<MembersResponseData>
> => ({
  type: UPDATE_SCHEDULED_PAYMENT,
  payload: callApi(
    process.env.REACT_APP_API + '/payment/scheduled',
    true,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ id, chargeDate, amount })
    }
  )
});

export const createPayment: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  id: number,
  paymentType: string,
  downPayment: number,
  total: number,
  amountPerTransaction: number,
  currency: Currency | '',
  term: string,
  numberOfTransactions: number,
  startDate: number,
  payerId: number,
  memberId: number,
  paymentMethodId: number,
  continuous: boolean,
  schedules: {
    scheduledPaymentId: number,
    chargeDate: number,
    amount: number,
    status: string
  }[],
  description?: string
): ReduxPromiseAction<
  MembersActionTypes, SimpleResponse<MembersResponseData>
> => ({
  type: CREATE_PAYMENT,
  payload: callApi(
    process.env.REACT_APP_API + '/payment',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        id,
        paymentType,
        downPayment,
        total,
        amountPerTransaction,
        currency,
        term,
        numberOfTransactions,
        startDate,
        payerId,
        memberId,
        paymentMethodId,
        continuous,
        schedules,
        description
      })
    }
  )
});

export const updatePayment: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  id: number,
  payerId: number,
  paymentMethodId: number,
  continuous: boolean,
  amountPerTransaction: number,
  schedules: {
    scheduledPaymentId: number,
    chargeDate: number,
    amount: number,
    status: string
  }[],
  description?: string
): ReduxPromiseAction<
  MembersActionTypes, SimpleResponse<MembersResponseData>
> => ({
  type: UPDATE_PAYMENT,
  payload: callApi(
    process.env.REACT_APP_API + '/payment',
    true,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({
        id,
        payerId,
        paymentMethodId,
        continuous,
        amountPerTransaction,
        schedules,
        description
      })
    }
  )
});

export const cancelMemberPayment: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (paymentId: number): ReduxPromiseAction<
  MembersActionTypes, SimpleResponse<MembersResponseData>
> => ({
  type: CANCEL_PAYMENT,
  payload: callApi(
    process.env.REACT_APP_API + '/payment/' + paymentId,
    true,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});

export const getMembers: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: GET_MEMBERS,
  payload: callApi(
    process.env.REACT_APP_API + '/member/list',
    true,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});

export const getMember: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (memberId: number): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: GET_MEMBER,
  payload: callApi(
    process.env.REACT_APP_API + '/member/' + memberId,
    true,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      }
    }
  )
});

export const createMember: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  file: File | null,
  firstName: string,
  lastName: string,
  initial: string,
  gender: string,
  birthDate: number,
  email: string,
  address: string,
  city: string,
  province: string,
  country: string,
  postalCode: string,
  mobile1: string,
  mobile2: string | undefined,
  startDate: number,
  endDate: number,
  emergencyContactName1: string | undefined,
  emergencyContactNumber1: string | undefined,
  emergencyContactName2: string | undefined,
  emergencyContactNumber2: string | undefined,
  emergencyContactName3: string | undefined,
  emergencyContactNumber3: string | undefined,
  checkinNotification: string,
  contractDuration: string,
  maxAttendancePerWeek: number,
  point: number,
  note: string,
  emailConsent: Boolean,
  smsConsent: Boolean,
  remainingClasses: number,
  memberType: MemberType,
  tags: string[]
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: REGISTER_MEMBER,
  payload: callApi(
    process.env.REACT_APP_API + '/member',
    true,
    {
      method: 'POST',
      headers: {
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: ((): FormData => {
        let formData = new FormData();
        formData.append(
          'data',
          JSON.stringify({
            firstName,
            lastName,
            initial,
            gender,
            birthDate,
            email,
            address,
            city,
            province,
            country,
            postalCode,
            mobile1,
            mobile2,
            startDate,
            endDate,
            emergencyContactName1,
            emergencyContactNumber1,
            emergencyContactName2,
            emergencyContactNumber2,
            emergencyContactName3,
            emergencyContactNumber3,
            checkinNotification,
            contractDuration,
            maxAttendancePerWeek,
            point,
            note,
            emailConsent,
            smsConsent,
            remainingClasses,
            memberType,
            tags
          })
        );

        formData.append('file', (file) ? file : '');

        return formData;
      })()
    }
  )
});

export const updateMember: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  id: number,
  file: File | null,
  firstName: string,
  lastName: string,
  initial: string,
  gender: string,
  birthDate: number,
  email: string,
  address: string,
  city: string,
  province: string,
  country: string,
  postalCode: string,
  mobile1: string,
  mobile2: string | undefined,
  startDate: number,
  endDate: number,
  emergencyContactName1: string | undefined,
  emergencyContactNumber1: string | undefined,
  emergencyContactName2: string | undefined,
  emergencyContactNumber2: string | undefined,
  emergencyContactName3: string | undefined,
  emergencyContactNumber3: string | undefined,
  checkinNotification: string,
  contractDuration: string,
  maxAttendancePerWeek: number,
  point: number,
  note: string,
  emailConsent: Boolean,
  smsConsent: Boolean,
  cancelled: boolean,
  remainingClasses: number,
  memberType: MemberType,
  tags: string[],
  cancelPayment: boolean,
  sendNotificationToMember: boolean
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: UPDATE_MEMBER,
  payload: callApi(
    process.env.REACT_APP_API + '/member',
    true,
    {
      method: 'PATCH',
      headers: {
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: ((): FormData => {
        let formData = new FormData();
        formData.append(
          'data',
          JSON.stringify({
            id,
            firstName,
            lastName,
            initial,
            gender,
            birthDate,
            email,
            address,
            city,
            province,
            country,
            postalCode,
            mobile1,
            mobile2,
            startDate,
            endDate,
            emergencyContactName1,
            emergencyContactNumber1,
            emergencyContactName2,
            emergencyContactNumber2,
            emergencyContactName3,
            emergencyContactNumber3,
            checkinNotification,
            contractDuration,
            maxAttendancePerWeek,
            point,
            note,
            emailConsent,
            smsConsent,
            cancelled,
            remainingClasses,
            memberType,
            tags,
            cancelPayment,
            sendNotificationToMember
          })
        );

        formData.append('file', (file) ? file : '');

        return formData;
      })()
    }
  )
});

export const bulkCancel: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  memberIds: number[],
  cancelMembership: boolean,
  cancelPayment: boolean
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: BULK_CANCEL,
  payload: callApi(
    process.env.REACT_APP_API + '/member/actions',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ memberIds, cancelMembership, cancelPayment })
    }
  )
});

export const bulkAddDaysToEndDates: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  memberIds: number[],
  days: number
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: BULK_ADD_DAYS_TO_END_DATES,
  payload: callApi(
    process.env.REACT_APP_API + '/member/actions',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ memberIds, days })
    }
  )
});

export const bulkSendEmail: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  memberIds: number[],
  emailTemplateId: number
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: BULK_SEND_EMAILS,
  payload: callApi(
    process.env.REACT_APP_API + '/member/actions',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ memberIds, emailTemplateId })
    }
  )
});

export const bulkSendSms: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  memberIds: number[],
  smsBody: string,
  sendMobile2: boolean
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: BULK_SEND_SMS,
  payload: callApi(
    process.env.REACT_APP_API + '/member/actions',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ memberIds, smsBody, sendMobile2 })
    }
  )
});

export const exportCsv: Redux.ActionCreator<
  ReduxPromiseAction<
    MembersActionTypes, SimpleResponse<MembersResponseData>
  >
> = (
  memberIds: number[]
): ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> => ({
  type: EXPORT_CSV,
  payload: callApi(
    process.env.REACT_APP_API + '/member/actions',
    true,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pickAuthOrImpersonationToken()
      },
      body: JSON.stringify({ memberIds, export: true })
    }
  )
});

export const getAbsentMembers:
  Redux.ActionCreator<ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>>> =
  (weeks: number):
    ReduxPromiseAction<MembersActionTypes, SimpleResponse<MembersResponseData>> =>
    ({
      type: GET_ABSENT_MEMBERS,
      payload: callApi(process.env.REACT_APP_API + '/member/absent/' + weeks,
        true,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': pickAuthOrImpersonationToken()
          }
        })
    });



// WEBPACK FOOTER //
// ./src/members/members.actions.tsx